import { AlertMessage } from "@/components/reusable";
import { ProviderSelector } from "@/components/providers/ProviderSelector";
import { isSchemaNameTaken } from "@/lib/schemaBuilderHelpers";
import { listSchemas } from "@/services/schemaBuilderApi";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

export function SchemaScratchSetupPage() {
  const navigate = useNavigate();
  const [schemaName, setSchemaName] = useState("");
  const [providerId, setProviderId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [isChecking, setIsChecking] = useState(false);

  const canStart = schemaName.trim().length > 0 && providerId !== null;

  async function handleStartBuilding() {
    if (!canStart) return;

    setError("");
    setIsChecking(true);
    try {
      const schemas = await listSchemas(true);
      if (isSchemaNameTaken(schemaName, schemas)) {
        setError("A schema with this name already exists. Please choose a different name.");
        return;
      }
    } finally {
      setIsChecking(false);
    }

    navigate("/schema-builder/new", {
      state: {
        source: "scratch",
        schemaName: schemaName.trim(),
        providerId,
      },
    });
  }

  return (
    <div className="min-h-[calc(100vh-72px)] animate-fade-in">
      <section className="px-12 py-10">
        <button
          type="button"
          onClick={() => navigate("/schema-builder/create")}
          className="mb-8 flex items-center gap-2 text-sm font-medium text-[var(--color-text-primary)] transition hover:text-[var(--color-brand-primary)]"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <h1 className="mb-8 text-3xl font-semibold tracking-tight text-[var(--color-text-primary)]">
          Start from Scratch
        </h1>

        <div className="max-w-[520px] space-y-6">
          <div>
            <label className="mb-2 block text-sm font-semibold text-[var(--color-text-primary)]">
              Provider
            </label>
            <ProviderSelector value={providerId} onChange={setProviderId} />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-[var(--color-text-primary)]">
              Name Your Schema
            </label>

            <input
              value={schemaName}
              onChange={(event) => {
                setSchemaName(event.target.value);
                setError("");
              }}
              placeholder="Schema Name ABC"
              className="h-10 w-full rounded-md border border-[var(--color-border)] px-3 text-sm outline-none transition focus:border-[var(--color-action-primary)]"
            />

            {error && <AlertMessage variant="error" message={error} className="mt-3" />}
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleStartBuilding}
              disabled={!canStart || isChecking}
              className="rounded-md bg-[var(--color-action-primary)] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[var(--color-action-primary-hover)] disabled:bg-[var(--color-bg-secondary)] disabled:text-[var(--color-text-tertiary)]"
            >
              {isChecking ? "Checking..." : "Start Building"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
